/*
  [DTC-1580] Web app link attribution (ref / from / UTM pass-through).

  Every <a> pointing at web.plaud.ai or app.plaud.ai gets, at the moment it is rendered and again on
  interaction:
    - utm_* + creator + ad click / affiliate IDs: the set the visitor landed on this store with.
      UTMs typed on the link itself are removed first, so DTC never sends its own; with nothing
      captured the link carries no UTMs. Click IDs are passed as-is, never added or invented.
    - ref:  kept when the link already has one (team, referral, shop-plaud, menu, sales-team...);
            otherwise data-web-ref on the link, data-web-ref-scope on an ancestor (header = menu,
            footer = footer), or the name of the section the link sits in.
    - from: always <store>_dtc_<page> (us_dtc_team_page...), replacing any typed value.
  Download, /privacy, /s/ and /share/ links are left alone.

  Landing set: the first page view that carries any PASS_KEYS parameter (UTMs, creator, gclid /
  gbraid / wbraid / fbclid / msclkid / ttclid / li_fat_id, irclickid / sscid) replaces the whole set. It is kept in localStorage for
  30 minutes of inactivity, only once cookie consent allows it (consentmanager purpose, default c54,
  or regulation 0 = no consent law); same rule as DTC-1034. Without consent, links are tagged from the
  current URL only, so a click straight from the landing page is still attributed.

  Config comes from snippets/web-app-link-attribution.liquid (window.plaudWebAttrConfig), which also
  keeps this file out of the Theme Editor. Pure URL helpers are exported for
  tests/web-app-link-attribution.test.mjs.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  if (root && root.document) {
    try { api.boot(root); } catch (e) { /* fail safe: links stay as they are */ }
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var WEB_APP_HOSTS = ['web.plaud.ai', 'app.plaud.ai'];
  var LINK_SELECTOR = 'a[href*="web.plaud.ai" i], a[href*="app.plaud.ai" i]';
  /* Ad click IDs + affiliate IDs follow the UTM rule (agreed with Data Product on DAT-707, 2026-10-19). */
  var CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id', 'irclickid', 'sscid'];
  var PASS_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'creator'].concat(CLICK_ID_KEYS);
  var STORE_CODES = ['us', 'ca', 'uk', 'eu', 'de', 'fr', 'es', 'it', 'au', 'sea', 'jp', 'hk', 'tw', 'in', 'uae', 'latam', 'global'];
  var HOST_ALIASES = { 'plaud.ai': 'us', 'www.plaud.ai': 'us', 'sg.plaud.ai': 'sea' };
  /* Names already in the data for these pages; other pages use their handle. */
  var PAGE_ALIASES = {
    'plaud-team': 'team_page',
    'plaud-ai-plan-pricing': 'pricing_page',
    'plaud-team-tour': 'producttour'
  };
  var MAX_VALUE_LENGTH = 512; /* click IDs run long; truncating one breaks it */
  var STORAGE_KEY = 'plaud_web_attr';
  var STORAGE_TTL_MS = 30 * 60 * 1000;

  /* ---------- pure helpers (unit tested) ---------- */

  function storeCodeFromHost(host) {
    host = String(host || '').toLowerCase().replace(/\.$/, '');
    if (HOST_ALIASES[host]) return HOST_ALIASES[host];
    var m = host.match(/^([a-z0-9-]+)\.plaud\.ai$/);
    if (!m) return '';
    return STORE_CODES.indexOf(m[1]) !== -1 ? m[1] : m[1].replace(/-/g, '_');
  }

  /* override (theme setting) > domain > 'test' (preview, *.myshopify.com), so test clicks never count as a store. */
  function resolveStoreCode(host, override) {
    var code = String(override || '').trim().toLowerCase();
    return code || storeCodeFromHost(host) || 'test';
  }

  function pageKey(template, handle) {
    template = String(template || '').toLowerCase();
    handle = String(handle || '').toLowerCase();
    if (template === 'index') return 'home';
    if (template === 'article' || template === 'blog') return 'blog';
    if (template === 'page' && PAGE_ALIASES[handle]) return PAGE_ALIASES[handle];
    if ((template === 'page' || template === 'product' || template === 'collection') && handle) return handle;
    return template || 'unknown';
  }

  /* shopify-section-template--123__delta_presale_page_TUJRC8 -> delta_presale_page */
  function sectionName(id) {
    var key = String(id || '').replace(/^shopify-section-/, '');
    var cut = key.lastIndexOf('__');
    if (cut !== -1) key = key.slice(cut + 2);
    /* Keys added in the editor are <type>_<6 random chars>; the random part has a capital or a digit. */
    var m = key.match(/^(.*[a-z].*)_([A-Za-z0-9]{6})$/);
    if (m && /[A-Z0-9]/.test(m[2])) key = m[1];
    if (/^\d{6,}/.test(key)) return ''; /* app blocks: numeric ids say nothing about the button */
    return key.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function readLanding(search) {
    var params;
    try { params = new URLSearchParams(search || ''); } catch (e) { return null; }
    var out = null;
    for (var i = 0; i < PASS_KEYS.length; i++) {
      var v = params.get(PASS_KEYS[i]);
      v = v == null ? '' : String(v).trim().slice(0, MAX_VALUE_LENGTH);
      if (v) {
        out = out || {};
        out[PASS_KEYS[i]] = v;
      }
    }
    return out;
  }

  function isExcludedPath(path) {
    return /^\/(download|privacy)(\/|$)/i.test(path) || /^\/(s|share)\//i.test(path);
  }

  /*
    Returns the tagged href, or null when the link is not a taggable web app link.
    ctx: { base, store, page, ref, landing }. Idempotent: tagging a tagged href gives the same href.
  */
  function decorateHref(href, ctx) {
    ctx = ctx || {};
    var url;
    try { url = new URL(String(href || '').trim(), ctx.base || undefined); } catch (e) { return null; }
    if (WEB_APP_HOSTS.indexOf(url.hostname.toLowerCase()) === -1) return null;
    if (url.protocol === 'http:') url.protocol = 'https:';
    if (url.protocol !== 'https:') return null;
    if (isExcludedPath(url.pathname)) return null;

    var params = url.searchParams;
    var landing = ctx.landing || {};
    var typed = [];
    params.forEach(function (value, key) { typed.push(key); });
    for (var i = 0; i < typed.length; i++) {
      if (/^utm_/i.test(typed[i])) params.delete(typed[i]);
    }
    /* creator / click IDs typed on the link survive unless the landing brought one; delete first so re-tagging keeps the order. */
    for (var k = 0; k < PASS_KEYS.length; k++) {
      if (landing[PASS_KEYS[k]]) params.delete(PASS_KEYS[k]);
    }
    if (!params.get('ref') && ctx.ref) params.set('ref', ctx.ref);
    if (ctx.store && ctx.page) params.set('from', ctx.store + '_dtc_' + ctx.page);
    for (var j = 0; j < PASS_KEYS.length; j++) {
      if (landing[PASS_KEYS[j]]) params.set(PASS_KEYS[j], landing[PASS_KEYS[j]]);
    }
    /* /applink only forwards the visitor to its web_url and drops every other parameter, so the
       destination inside web_url is tagged too (UAE purchase buttons, CA referral button). */
    if (/^\/applink\/?$/i.test(url.pathname) && params.get('web_url')) {
      var inner = decorateHref(params.get('web_url'), ctx);
      if (inner) params.set('web_url', inner);
    }
    return url.toString();
  }

  /* consentmanager: regulation 0 -> accepted; otherwise a choice was made and the purpose is granted. */
  function consentAccepted(data, status, purpose) {
    if (Number(data && data.regulation) === 0) return true;
    var purposes = (data && data.purposeConsents) || {};
    return !!(status && status.userChoiceExists) && purposes[purpose] === true;
  }

  function normalizePurpose(purpose) {
    purpose = String(purpose || '').trim().toLowerCase();
    if (/^\d+$/.test(purpose)) purpose = 'c' + purpose;
    return purpose || 'c54';
  }

  /* ---------- browser wiring ---------- */

  function boot(w) {
    var d = w.document;
    var cfg = w.plaudWebAttrConfig || {};
    if (w.plaudWebAttribution || cfg.enabled === false) return;
    if (w.Shopify && w.Shopify.designMode) return;
    if (typeof w.URL !== 'function' || typeof w.URLSearchParams !== 'function') return;

    var STORE = resolveStoreCode(w.location.hostname, cfg.storeCode);
    var PAGE = pageKey(cfg.template, cfg.handle);
    var PURPOSE = normalizePurpose(cfg.consentPurpose);
    var HAS_CMP = cfg.hasCmp === true;
    var CMP_EVENTS = ['settings', 'consent', 'consentapproved', 'consentrejected', 'consentcustom', 'loadNotShowing'];
    var CMP_POLL_MS = 100;
    var CMP_POLL_FAST_MAX = 300; /* 100 ms for 30 s, then 1 s: cmp.liquid is lazy-loaded by speed.liquid */
    var CMP_POLL_SLOW_MS = 1000;

    var urlLanding = readLanding(w.location.search);
    var state = { consent: !HAS_CMP, consentKnown: !HAS_CMP, landing: urlLanding };
    var pending = false;

    function readStored() {
      try {
        var raw = w.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        var entry = JSON.parse(raw);
        if (!entry || typeof entry.t !== 'number' || !entry.p || Date.now() - entry.t > STORAGE_TTL_MS) {
          w.localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        return readLanding(new URLSearchParams(entry.p).toString());
      } catch (e) {
        return null;
      }
    }

    function writeStored(landing) {
      try { w.localStorage.setItem(STORAGE_KEY, JSON.stringify({ p: landing, t: Date.now() })); } catch (e) { /* storage off */ }
    }

    function dropStored() {
      try { w.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    }

    /* A new landing replaces the whole set; a page view without one keeps (and refreshes) the stored set. */
    function resolveLanding() {
      if (!state.consent) {
        state.landing = urlLanding;
        return;
      }
      if (urlLanding) {
        state.landing = urlLanding;
      } else {
        state.landing = readStored();
      }
      if (state.landing) writeStored(state.landing);
    }

    function placementRef(a) {
      var own = a.getAttribute('data-web-ref');
      if (own) return own;
      var scope = a.closest('[data-web-ref-scope]');
      if (scope) return scope.getAttribute('data-web-ref-scope');
      var section = a.closest('[id^="shopify-section-"]');
      return section ? sectionName(section.id) : '';
    }

    function decorateLink(a) {
      try {
        var current = a.getAttribute('href');
        if (!current) return;
        var next = decorateHref(current, {
          base: w.location.href,
          store: STORE,
          page: PAGE,
          ref: placementRef(a),
          landing: state.landing
        });
        if (next && next !== current) a.setAttribute('href', next);
      } catch (e) { /* leave this link as it is */ }
    }

    function decorateAll(rootNode) {
      if (!rootNode || !rootNode.querySelectorAll) return;
      if (rootNode.nodeType === 1 && rootNode.tagName === 'A') decorateLink(rootNode);
      var links = rootNode.querySelectorAll(LINK_SELECTOR);
      for (var i = 0; i < links.length; i++) decorateLink(links[i]);
    }

    function scheduleDecorate() {
      if (pending) return;
      pending = true;
      /* setTimeout, not requestAnimationFrame: rAF is paused in background tabs. */
      w.setTimeout(function () {
        pending = false;
        decorateAll(d);
      }, 0);
    }

    function onMutations(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          if (m.target && m.target.tagName === 'A') decorateLink(m.target);
          continue;
        }
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType === 1) decorateAll(node);
        }
      }
    }

    /* Safety net: re-tag right before the browser follows (or copies) the link. */
    function onInteraction(e) {
      var t = e.target;
      if (t && t.nodeType === 3) t = t.parentElement;
      if (!t || typeof t.closest !== 'function') return;
      var a = t.closest('a[href]');
      if (a) decorateLink(a);
    }

    /* ---------- cookie consent (consentmanager), as in DTC-1034 ---------- */
    var cmpBound = false;
    var cmpRebound = false;
    var cmpSettled = false;
    var cmpTries = 0;
    var cmpTimer = 0;

    function onCmpEvent() {
      cmpSettled = true;
      evaluateConsent();
    }

    function bindCmpEvents() {
      for (var i = 0; i < CMP_EVENTS.length; i++) {
        try { w.__cmp('addEventListener', [CMP_EVENTS[i], onCmpEvent, false], null); } catch (e) { /* stub */ }
      }
    }

    function evaluateConsent() {
      if (typeof w.__cmp !== 'function') return false;
      var data, status;
      try {
        data = w.__cmp('getCMPData');
        status = w.__cmp('consentStatus');
      } catch (e) {
        return false;
      }
      if (!data || typeof data !== 'object' || !('regulation' in data)) return false;
      /* Before its config loads, the real __cmp reports a placeholder regulation 0 with no purposes. */
      var configured = cmpSettled || (Array.isArray(data.purposesList) && data.purposesList.length > 0);
      if (!configured) return false;
      var accepted = consentAccepted(data, status, PURPOSE);
      var changed = !state.consentKnown || state.consent !== accepted;
      var withdrawn = state.consentKnown && state.consent && !accepted;
      state.consentKnown = true;
      state.consent = accepted;
      if (withdrawn) dropStored();
      if (changed) {
        resolveLanding();
        scheduleDecorate();
      }
      return true;
    }

    function waitForCmp() {
      cmpTimer = 0;
      if (typeof w.__cmp === 'function') {
        if (!cmpBound) {
          cmpBound = true;
          bindCmpEvents();
        }
        if (evaluateConsent()) {
          if (!cmpRebound) {
            cmpRebound = true;
            bindCmpEvents();
          }
          return;
        }
      }
      cmpTries++;
      cmpTimer = setTimeout(waitForCmp, cmpTries < CMP_POLL_FAST_MAX ? CMP_POLL_MS : CMP_POLL_SLOW_MS);
    }

    function pokeCmp() {
      if (cmpTimer) {
        clearTimeout(cmpTimer);
        cmpTimer = 0;
        waitForCmp();
      }
    }

    w.plaudWebAttribution = {
      store: STORE,
      page: PAGE,
      get: function () {
        return { store: STORE, page: PAGE, consent: state.consent, consentKnown: state.consentKnown, landing: state.landing };
      },
      decorate: function (rootNode) { decorateAll(rootNode || d); }
    };

    resolveLanding();
    if (typeof w.MutationObserver === 'function') {
      new w.MutationObserver(onMutations).observe(d.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['href']
      });
    }
    var events = ['pointerdown', 'mousedown', 'keydown', 'click', 'auxclick', 'contextmenu', 'dragstart'];
    for (var i = 0; i < events.length; i++) d.addEventListener(events[i], onInteraction, true);
    decorateAll(d);
    if (HAS_CMP) {
      d.addEventListener('asyncLazyLoad', pokeCmp);
      waitForCmp();
    }
  }

  return {
    PASS_KEYS: PASS_KEYS,
    CLICK_ID_KEYS: CLICK_ID_KEYS,
    STORE_CODES: STORE_CODES,
    storeCodeFromHost: storeCodeFromHost,
    resolveStoreCode: resolveStoreCode,
    pageKey: pageKey,
    sectionName: sectionName,
    readLanding: readLanding,
    isExcludedPath: isExcludedPath,
    decorateHref: decorateHref,
    consentAccepted: consentAccepted,
    normalizePurpose: normalizePurpose,
    boot: boot
  };
});
